import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography,
  makeStyles,
} from "@material-ui/core";
import RestaurantMenuRoundedIcon from "@material-ui/icons/RestaurantMenuRounded";
import DateRangeRoundedIcon from "@material-ui/icons/DateRangeRounded";
import ScheduleRoundedIcon from "@material-ui/icons/ScheduleRounded";
import PageTitle from "../../components/PageTitle/PageTitle";
import SchoolFoodMenuService from "./Locality/Service/schoolFoodMenuService";

const useStyles = makeStyles((theme) => ({
  rootGrid: {
    alignItems: "stretch",
  },
  menuCard: {
    borderRadius: 14,
    border: "1px solid rgba(15, 59, 98, 0.12)",
    background: "linear-gradient(145deg, #ffffff 0%, #f6f9fd 100%)",
    boxShadow: "0 8px 28px rgba(15, 59, 98, 0.08)",
    height: "100%",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  headingWrap: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  iconWrap: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "rgba(15, 59, 98, 0.1)",
    display: "grid",
    placeItems: "center",
    color: "#0f3b62",
  },
  menuName: {
    fontWeight: 700,
    color: "#203040",
    marginTop: theme.spacing(1.2),
    marginBottom: theme.spacing(1.8),
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.2),
  },
  softChip: {
    backgroundColor: "rgba(15, 59, 98, 0.09)",
    color: "#0f3b62",
    fontWeight: 600,
  },
  statusActive: {
    backgroundColor: "rgba(67, 160, 71, 0.14)",
    color: "#2e7d32",
    fontWeight: 700,
  },
  statusInactive: {
    backgroundColor: "rgba(229, 57, 53, 0.12)",
    color: "#c62828",
    fontWeight: 700,
  },
  helperText: {
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1.2),
  },
}));

export default function ParentFoodMenu() {
  const classes = useStyles();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    let mounted = true;
    SchoolFoodMenuService.getParentActive()
      .then((res) => {
        if (!mounted) return;
        setMenu(res || null);
        setHasError(false);
      })
      .catch(() => {
        if (!mounted) return;
        setMenu(null);
        setHasError(true);
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const itemNames = Array.isArray(menu?.itemNames)
    ? menu.itemNames.map((n) => String(n || "").trim()).filter(Boolean)
    : [];
  const foodItemName = String(menu?.foodItemName || "").trim();
  const displayFoodText = itemNames.length ? itemNames.join(", ") : foodItemName;
  const status = String(menu?.status || "Active");
  const displayDate = new Date().toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const formatYmdToFriendly = (v) => {
    const ymd = String(v || "").slice(0, 10);
    if (!ymd) return "";
    const d = new Date(ymd);
    if (Number.isNaN(d.getTime())) return ymd;
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };
  const fromText = menu?.fromDate ? formatYmdToFriendly(menu.fromDate) : "";
  const toText = menu?.toDate ? formatYmdToFriendly(menu.toDate) : "";
  const periodText = menu?.fromDate || menu?.toDate ? `${fromText || "Any"} → ${toText || "Any"}` : "";
  const periodLabel =
    menu?.fromDate || menu?.toDate ? `Valid from ${fromText || "Any day"} to ${toText || "Any day"}` : "Valid for all days";
  const hasDateRange = !!(menu?.fromDate || menu?.toDate);
  const hasClassWise = !!menu?.classWise;
  const className = String(menu?.classId?.className || "").trim();
  const hasDayWise = !!menu?.dayWise;
  const dayName = String(menu?.dayName || "").trim();

  return (
    <>
      <PageTitle title="Food Menu" />
      <Grid container spacing={4} className={classes.rootGrid}>
        <Grid item xs={12} md={8}>
          <Card className={classes.menuCard}>
            <CardContent>
              <Box className={classes.cardHeader}>
                <Box className={classes.headingWrap}>
                  <Box className={classes.iconWrap}>
                    <RestaurantMenuRoundedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography variant="h6">Today&apos;s Food Menu</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Healthy meals curated for children
                    </Typography>
                  </Box>
                </Box>
                <Chip
                  label={displayDate}
                  icon={<DateRangeRoundedIcon style={{ color: "#0f3b62" }} />}
                  className={classes.softChip}
                />
              </Box>
              <Divider style={{ margin: "12px 0 14px" }} />
              {loading ? (
                <Typography variant="body2" className={classes.helperText}>
                  Loading today&apos;s menu...
                </Typography>
              ) : !displayFoodText ? (
                <Typography variant="body2" className={classes.helperText}>
                  {hasError
                    ? "Unable to load menu right now. Please try again in a moment."
                    : "No active menu found for today."}
                </Typography>
              ) : (
                <Box>
                  <Typography variant="h5" className={classes.menuName}>
                    {displayFoodText}
                  </Typography>
                  <Box className={classes.chipRow}>
                  
                    <Chip
                      label={periodLabel}
                      className={classes.softChip}
                    />
                    {hasDateRange ? <Chip label={`Range: ${periodText}`} className={classes.softChip} /> : null}
                    {hasDayWise ? (
                      <Chip
                        label={`Day wise: ${dayName || "-"}`}
                        icon={<ScheduleRoundedIcon style={{ color: "#0f3b62" }} />}
                        className={classes.softChip}
                      />
                    ) : null}
                    {hasClassWise ? (
                      <Chip label={`Class: ${className || "Particular class"}`} className={classes.softChip} />
                    ) : null}
                  </Box>
                  <Typography variant="caption" className={classes.helperText}>
                    Applied options:{" "}
                    {[
                      hasDateRange ? "Start/End Date" : null,
                      hasClassWise ? "Particular Class" : null,
                      hasDayWise ? "Day Wise" : null,
                    ]
                      .filter(Boolean)
                      .join(", ") || "General menu"}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}
